/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  RouterStateSnapshot
} from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '../../../state/app.state';
import { workspaceListFeature, WorkspaceListFetch } from '@workspaces/store/workspace-list.feature';
import { CognitoService } from '../../../services/cognito.service';
import { combineLatest, filter, from, map, of, startWith, switchMap, take } from 'rxjs';
import { getAllPathParams } from '../get-all-path-params';

@Injectable({
  providedIn: 'root'
})
export class WorkspaceMemberGuard implements CanActivate, CanActivateChild {
  constructor(
    protected readonly route: ActivatedRoute,
    protected readonly store: Store<AppState>,
    protected readonly cognitoService: CognitoService
  ) {
    this.fetchWorkspaces();
  }

  async fetchWorkspaces() {
    const isAdmin = await this.cognitoService.isAdmin();
    this.store.dispatch(WorkspaceListFetch({ include_archived: true, include_deleted: isAdmin }));
  }

  canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) {
    const pathParams = getAllPathParams(route);
    return this.activatorCheck(pathParams['workspaceId']).pipe(take(1));
  }

  canActivateChild(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) {
    const pathParams = getAllPathParams(route);
    return this.activatorCheck(pathParams['workspaceId']).pipe(take(1));
  }

  private activatorCheck(workspaceId: string) {
    return this.getUserInWorkplace(workspaceId).pipe(
      map(userInWorkspace => !!userInWorkspace && userInWorkspace.invitation_status.toLowerCase() === 'active')
    );
  }

  getUserInWorkplace(workspaceId: string) {
    return combineLatest([
      this.store.select(workspaceListFeature.selectData).pipe(filter(workspaces => workspaces.length !== 0)),
      this.store.select(workspaceListFeature.workspaceListsIsLoading).pipe(filter(isLoading => !isLoading))
    ]).pipe(
      switchMap(([workspaceData]) => {
        const userIdPromise = this.cognitoService.getUserId();

        return from(userIdPromise).pipe(
          map(userId => ({
            userId: userId,
            workspaceData: workspaceData
          }))
        );
      }),
      map(data => {
        const currentWorkSpace = data.workspaceData.find(workspace => {
          return workspace.id === workspaceId;
        });
        if (!currentWorkSpace) {
          return undefined;
        }
        const userInWorkspace = currentWorkSpace.members.find(
          member => member.id === data.userId || member.cognito_id === data.userId
        );
        return userInWorkspace;
      })
    );
  }
}
